/**
 * @file CustomersFilters.tsx
 * @description Free-text search + status-chip filters for the admin
 *   customers list. Filtering is client-side because listCustomers()
 *   already caps the result at 200 rows server-side, so the browser
 *   pays a trivial cost and the UI stays snappy. If the limit ever
 *   grows we'll move to server-side filtering.
 * @module Components
 * @security Search input is pure local state; the server already
 *   returned only the admin's authorized rows. We never echo the
 *   query string to the server in this slice.
 */

import type { ChangeEvent, JSX } from 'react';
import { useTranslation } from 'react-i18next';
import type { CustomerRow } from '../../lib/admin-api';
import { relativeTime } from '../../lib/relative-time';

export type StatusFilter = 'all' | 'active' | 'invited';
/** Slice 68 — coarse activity buckets driven by last_login_at:
 *  - active: signed in within the last 7 days (today/yesterday/days)
 *  - recent: 8-30 days ago (weeks bucket)
 *  - dormant: 30+ days ago (months/years buckets)
 *  - never: last_login_at is null
 *  - all: no filter
 *  Same bucket boundaries as the slice-66 chip so the UI is consistent. */
export type ActivityFilter = 'all' | 'active' | 'recent' | 'dormant' | 'never';

export interface CustomerFilters {
  query: string;
  status: StatusFilter;
  activity: ActivityFilter;
}

export const EMPTY_FILTERS: CustomerFilters = { query: '', status: 'all', activity: 'all' };

/** Pure — exported for tests + the chip's filtered-count display. */
export function activityBucket(at: string | null, now?: Date): ActivityFilter {
  if (at === null) return 'never';
  const rt = relativeTime(at, now);
  if (rt.bucket === 'today' || rt.bucket === 'yesterday' || rt.bucket === 'days') return 'active';
  if (rt.bucket === 'weeks') return 'recent';
  if (rt.bucket === 'months' || rt.bucket === 'years') return 'dormant';
  return 'all'; // 'invalid' — don't surface as anything specific.
}

/**
 * Pure filter — exported for testing. Trims/case-folds the query once
 * and short-circuits when ALL filters are at defaults so the common
 * case is allocation-free.
 */
export function applyCustomerFilters(rows: CustomerRow[], f: CustomerFilters): CustomerRow[] {
  const q = f.query.trim().toLowerCase();
  const statusOn = f.status !== 'all';
  const activityOn = f.activity !== 'all';
  if (q === '' && !statusOn && !activityOn) return rows;
  return rows.filter((c) => {
    if (statusOn && c.status !== f.status) return false;
    if (activityOn && activityBucket(c.last_login_at) !== f.activity) return false;
    if (q !== '') {
      const haystack = `${c.email ?? ''} ${c.full_name ?? ''}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });
}

interface CustomersFiltersProps {
  filters: CustomerFilters;
  onChange: (next: CustomerFilters) => void;
  totalCount: number;
  filteredCount: number;
  /** Slice 68 — all rows (pre-filter) so the activity chip can decide
   *  whether to render and show per-bucket counts. */
  allRows: CustomerRow[];
}

export function CustomersFilters(p: CustomersFiltersProps): JSX.Element {
  const { t } = useTranslation();
  const onQuery = (e: ChangeEvent<HTMLInputElement>): void => {
    p.onChange({ ...p.filters, query: e.target.value });
  };
  const setStatus = (status: StatusFilter): void => {
    p.onChange({ ...p.filters, status });
  };
  const filtered = p.filteredCount !== p.totalCount;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={p.filters.query}
          onChange={onQuery}
          placeholder={t('customers.filters.search_placeholder')}
          aria-label={t('customers.filters.search_label')}
          className="flex-1 min-w-[12rem] px-3 py-2 text-sm"
        />
        <FilterChip
          label={t('customers.filters.status_all')}
          active={p.filters.status === 'all'}
          onClick={() => setStatus('all')}
        />
        <FilterChip
          label={t('customers.filters.status_active')}
          active={p.filters.status === 'active'}
          onClick={() => setStatus('active')}
        />
        <FilterChip
          label={t('customers.filters.status_invited')}
          active={p.filters.status === 'invited'}
          onClick={() => setStatus('invited')}
        />
      </div>
      <ActivityFilterBar filters={p.filters} onChange={p.onChange} allRows={p.allRows} />
      {filtered ? (
        <p className="text-xs text-slate-500">
          {t('customers.filters.shown_of_total', {
            shown: p.filteredCount,
            total: p.totalCount,
          })}
        </p>
      ) : null}
    </div>
  );
}

interface ActivityFilterBarProps {
  filters: CustomerFilters;
  onChange: (next: CustomerFilters) => void;
  allRows: CustomerRow[];
}

/** Slice 68 — second chip row, hidden when no row falls into a non-"all"
 *  activity bucket (e.g. a brand-new tenant with only "active" customers
 *  doesn't need the noise). */
function ActivityFilterBar({
  filters,
  onChange,
  allRows,
}: ActivityFilterBarProps): JSX.Element | null {
  const { t } = useTranslation();
  const counts = countByActivity(allRows);
  // Only render if at least one bucket beyond "active" has rows —
  // otherwise the bar is information-free clutter.
  if (counts.recent + counts.dormant + counts.never === 0) return null;
  const set = (activity: ActivityFilter): void => onChange({ ...filters, activity });
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs text-slate-500">{t('customers.filters.activity_label')}:</span>
      <FilterChip
        label={`${t('customers.filters.activity_all')} (${counts.all})`}
        active={filters.activity === 'all'}
        onClick={() => set('all')}
      />
      <FilterChip
        label={`${t('customers.filters.activity_active')} (${counts.active})`}
        active={filters.activity === 'active'}
        onClick={() => set('active')}
      />
      {counts.recent > 0 ? (
        <FilterChip
          label={`${t('customers.filters.activity_recent')} (${counts.recent})`}
          active={filters.activity === 'recent'}
          onClick={() => set('recent')}
        />
      ) : null}
      {counts.dormant > 0 ? (
        <FilterChip
          label={`${t('customers.filters.activity_dormant')} (${counts.dormant})`}
          active={filters.activity === 'dormant'}
          onClick={() => set('dormant')}
        />
      ) : null}
      {counts.never > 0 ? (
        <FilterChip
          label={`${t('customers.filters.activity_never')} (${counts.never})`}
          active={filters.activity === 'never'}
          onClick={() => set('never')}
        />
      ) : null}
    </div>
  );
}

/** Pure — exported for tests. */
export function countByActivity(rows: CustomerRow[]): {
  all: number;
  active: number;
  recent: number;
  dormant: number;
  never: number;
} {
  const c = { all: rows.length, active: 0, recent: 0, dormant: 0, never: 0 };
  for (const r of rows) {
    const b = activityBucket(r.last_login_at);
    if (b === 'active') c.active += 1;
    else if (b === 'recent') c.recent += 1;
    else if (b === 'dormant') c.dormant += 1;
    else if (b === 'never') c.never += 1;
  }
  return c;
}

interface FilterChipProps {
  label: string;
  active: boolean;
  onClick: () => void;
}

/** Generic pill chip shared by the status row + the activity row. */
function FilterChip({ label, active, onClick }: FilterChipProps): JSX.Element {
  const cls = active
    ? 'bg-brand text-brand-fg border-brand'
    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`text-xs border rounded-full px-3 py-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand ${cls}`}
    >
      {label}
    </button>
  );
}
